import React, { useEffect, useState } from 'react';
import Layout from '../layout/Layout';
import { usePageContent } from '../../context/ContentContext';

const ContactPage = () => {
    const { t, g } = usePageContent('contact');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Create mailto link with form data
        const subject = encodeURIComponent(`Contact from ${formData.name} - ${formData.company}`);
        const body = encodeURIComponent(
            `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCompany: ${formData.company}\n\nMessage:\n${formData.message}`
        );
        window.location.href = `mailto:${g('contact.email')}?subject=${subject}&body=${body}`;
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Lighter overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 leading-[60pt] tracking-[-0.02em] text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="flex items-center justify-center gap-4 mb-8">
                                <div className="w-8 h-8">
                                    <svg viewBox="0 0 100 100" className="w-full h-full">
                                        <circle cx="50" cy="50" r="40" fill="#fc1048" />
                                        <path d="M30 50 Q50 20 70 50 Q50 80 30 50" fill="white" />
                                    </svg>
                                </div>
                                <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-primary-dark-red">
                                    {t('form.heading')}
                                </h2>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-background-cool-blue p-12 rounded-lg mb-12">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block font-poppins font-semibold text-h3 mb-2">
                                            {t('form.nameLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-primary-red font-poppins font-normal text-body"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-poppins font-semibold text-h3 mb-2">
                                            {t('form.emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-primary-red font-poppins font-normal text-body"
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block font-poppins font-semibold text-h3 mb-2">
                                            {t('form.phoneLabel')}
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-primary-red font-poppins font-normal text-body"
                                        />
                                    </div>
                                    <div>
                                        <label className="block font-poppins font-semibold text-h3 mb-2">
                                            {t('form.companyLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-primary-red font-poppins font-normal text-body"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block font-poppins font-semibold text-h3 mb-2">
                                        {t('form.messageLabel')}
                                    </label>
                                    <textarea
                                        name="message"
                                        required
                                        rows="6"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-primary-red font-poppins font-normal text-body"
                                    ></textarea>
                                </div>

                                <div className="text-center">
                                    <button
                                        type="submit"
                                        className="px-8 py-4 bg-primary-red text-white hover:bg-primary-dark-red transition-colors duration-300 font-poppins font-semibold text-h3"
                                    >
                                        {t('form.submitText')}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Main Contact Info */}
                        <div className="text-center space-y-3 mb-12">
                            <a
                                href={`mailto:${g('contact.email')}`}
                                className="block font-poppins font-normal text-body text-primary-red hover:text-primary-dark-red transition-colors"
                            >
                                {g('contact.email')}
                            </a>
                            <div className="font-poppins font-normal text-body text-gray-700">
                                {t('details.address').map((line, index) => (
                                    <p key={index}>{line}</p>
                                ))}
                            </div>
                            <a
                                href={`tel:${g('contact.phoneLink')}`}
                                className="block font-poppins font-normal text-body text-primary-red hover:text-primary-dark-red transition-colors"
                            >
                                {g('contact.phone')}
                            </a>
                        </div>

                        {/* Individual Contact Cards */}
                        <div className="grid md:grid-cols-2 gap-8">
                            {t('people.cards').map((person, index) => (
                                <div key={index} className="bg-accent-powder-green py-16 px-8 rounded-lg shadow-sm">
                                    <h3 className="font-poppins font-semibold text-h3 mb-4">{person.name}</h3>
                                    <p className="font-poppins font-normal text-sub text-gray-600 mb-4">{person.role}</p>
                                    <a
                                        href={`mailto:${person.email}`}
                                        className="block font-poppins font-normal text-sub text-primary-red hover:text-primary-dark-red mb-2 transition-colors"
                                    >
                                        {person.email}
                                    </a>
                                    <a
                                        href={`tel:${person.phoneLink}`}
                                        className="block font-poppins font-normal text-sub text-primary-red hover:text-primary-dark-red transition-colors"
                                    >
                                        {person.phone}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Visit Us Section */}
            <section className="py-24 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-12 text-primary-dark-red">
                        {t('visit.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto text-center">
                        <p className="font-poppins font-normal text-body leading-[13pt] tracking-[0.01em] mb-12">
                            {t('visit.intro')}
                        </p>

                        <div className="bg-white p-12 rounded-lg shadow-sm">
                            <h3 className="font-poppins font-semibold text-h3 mb-6 text-primary-red">{t('visit.cardTitle')}</h3>
                            <p className="font-poppins font-normal text-body text-gray-700 mb-4">
                                <strong>{t('visit.addressTitle')}</strong><br />
                                {t('visit.address').map((line, index) => (
                                    <React.Fragment key={index}>
                                        {line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </p>
                            <p className="font-poppins font-normal text-sub text-gray-600 mb-6">
                                {t('visit.transport')}
                            </p>
                            <a
                                href={t('visit.mapUrl')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-8 py-3 bg-primary-red text-white hover:bg-primary-dark-red transition-colors duration-300 font-poppins font-semibold text-h3"
                            >
                                {t('visit.mapButtonText')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Working Hours */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 leading-[32pt] tracking-[-0.02em] text-center mb-12 text-primary-dark-red">
                        {t('hours.title')}
                    </h2>

                    <div className="max-w-2xl mx-auto">
                        <div className="bg-accent-powder-green p-8 rounded-lg">
                            <div className="space-y-4 text-center">
                                {t('hours.rows').map((row, index, rows) => (
                                    <div
                                        key={index}
                                        className={`flex justify-between items-center ${
                                            index === rows.length - 1 ? '' : 'border-b border-gray-200 pb-4'
                                        }`}
                                    >
                                        <span className="font-poppins font-normal text-body text-gray-700">{row.label}</span>
                                        <span className="font-poppins font-semibold text-body text-gray-900">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="font-poppins font-normal text-sub text-gray-600 text-center mt-6">
                                {t('hours.note')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default ContactPage;
